import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SavePromptModal from '../../../components/Modals/SavePromptModal';
import { MODALS, PERMISSIONS } from '../../../constants';
import { checkPermisson, getActiveModal, getNextNavigationLink } from '../../../selectors/common';
import { aSetNavigationInfo, aSetOpenModal } from '../../../reducers/common';
import ConfigurationRow from './ConfigurationsRow';
import { getConfigurationSettings, getUpdateConfigurationSettingsSuccess } from '../../../selectors/configurationSettings';
import { aGetConfigurationSettings, aUpdateConfigSettings } from '../../../reducers/configurationSettings';

const Configurations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeModal = useSelector(getActiveModal);
  const nextNavigationLink = useSelector(getNextNavigationLink);
  const hasPermissionToUpdate = useSelector((state => checkPermisson(state, PERMISSIONS.CONFIGURATION_SETTINGS_UPDATE)));
  const configurationsFromStore = useSelector(getConfigurationSettings);
  const updateConfigSuccess = useSelector(getUpdateConfigurationSettingsSuccess);

  const [configurationState, setConfigurationState] = useState({
    configurations: [],
    rowBeingEditted: null,
    hasChanges: false,
    errors: {},
  });

  function updateInputs(value, rowId, field) {
    const configurationsCopy = [...configurationState.configurations];
    configurationsCopy[rowId] = { ...configurationsCopy[rowId], [field]: value };
    setConfigurationState(prev => ({ ...prev, configurations: configurationsCopy, hasChanges: true }));
  }

  function discardChangesAndProceed() {
    dispatch(aSetOpenModal({ modal: '' }));
    dispatch(aSetNavigationInfo({ requiresSaveBeforeNavigation: false }));

    if (nextNavigationLink) {
      navigate(nextNavigationLink);
    }
  }

  function updateSelectedRow(rowId) {
    setConfigurationState(prev => ({ ...prev, rowBeingEditted: prev.rowBeingEditted === rowId ? null : rowId }));
  }

  function cancelChanges() {
    setConfigurationState({ rowBeingEditted: null, configurations: configurationsFromStore, hasChanges: false, errors: {} });
    dispatch(aSetNavigationInfo({ requiresSaveBeforeNavigation: false }));
  }

  function clearErrors() {
    setConfigurationState(prev => ({ ...prev, errors: {} }));
  }

  function isMaxOddsCapValid(value) {
    const isStringNumeric = /^(\d+(\.\d+)?|\.\d+)$/.test(value);

    if (!isStringNumeric) {
      return false;
    }

    const parsedValue = parseFloat(value);
    return !Number.isNaN(parsedValue) && parsedValue > 1.01 && parsedValue < 1000;
  }

  function saveChanges() {
    clearErrors();
    const MAX_ODDS_CAP_KEY = 'maxOddsCap';

    const data = configurationState.configurations[configurationState.rowBeingEditted];

    // only validate max odds cap configuration for now. Other configurations will be validated later todo
    if (data.param === MAX_ODDS_CAP_KEY && !isMaxOddsCapValid(data.value)) {
      setConfigurationState(prev => ({
        ...prev,
        errors: { [data.param]: 'Max Odds Cap must be a number between 1.01 and 1000' },
      }));
      return;
    }
    
    dispatch(aUpdateConfigSettings({ data }));

    if (activeModal) {
      dispatch(aSetOpenModal({ modal: '' }));
    }
  }
  
  useEffect(function initialFetchConfigurations() {
    dispatch(aGetConfigurationSettings());
  }, [dispatch]);

  useEffect(function synchronizeConfigurationsFromStore() {
    setConfigurationState(prev => ({ ...prev, configurations: configurationsFromStore }));
  }, [configurationsFromStore]);

  useEffect(function listenToHasChanges() {
    if (configurationState.hasChanges) {
      dispatch(aSetNavigationInfo({ requiresSaveBeforeNavigation: true }));
    }
  }, [configurationState.hasChanges, dispatch]);

  useEffect(function listenToUpdateConfigSuccess() {
    if (updateConfigSuccess) {
      setConfigurationState(prev => ({ ...prev, rowBeingEditted: null, hasChanges: false }));
      dispatch(aSetNavigationInfo({ requiresSaveBeforeNavigation: false }));
    }
  }, [updateConfigSuccess, dispatch]);
  
  return (
    <div className="table-holder">
      <div className="table-wrapper m-t-10">
        <div className='table-responsive fix-15'>

          <table className="table-style table-style--fixed">
            <thead>
              <tr>
                <th>Param</th>
                <th>Value</th>
                <th>Description</th>
                <th>Enabled</th>
                {hasPermissionToUpdate && (<th> </th>)}
              </tr>
            </thead>
            <tbody>
              {configurationState.configurations.map((configuration, idx) => (
                <ConfigurationRow
                  key={configuration.param}
                  configuration={configuration}
                  rowIndex={idx}
                  rowBeingEditted={configurationState.rowBeingEditted}
                  errors={configurationState.errors}
                  hasPermissionToUpdate={hasPermissionToUpdate}
                  updateInputs={updateInputs}
                  updateSelectedRow={updateSelectedRow}
                  saveChanges={saveChanges}
                  cancelChanges={cancelChanges}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {activeModal === MODALS.SAVE_BEFORE_NAVIGATION && (
        <SavePromptModal
          modalData={{
            title: 'Do you want to save the changes?',
            additionalMessages: ['Your changes will be lost if you don\'t save them'],
          }}
          cancel={discardChangesAndProceed}
          confirmation={saveChanges}
          confirm="Save"
          cancelled="Don't Save"
        />
      )}
    </div>
  );
};

export default Configurations;